import { ParsedTimefoldAnalysis, ParsedTimefoldConstraintRow } from './parseTimefoldScoreAnalysis';
import { Collision, ControlResult, InvolvedEntities, WarningPriority } from './types';

/**
 * Normalised finding model — one shape for findings coming from the control endpoint and
 * (later) the Timefold analysis, so the UI can render a single deduplicated list.
 * See docs/konzept-findings-highlighting.md §3.1 (Phase 2).
 *
 * This module is intentionally store-free and pure: it produces *candidates* from raw backend
 * payloads. Resolving labels / validating card uuids against the store happens in a thin
 * store-aware wrapper, and the panel UI builds on top of this.
 */

export type FindingSource = 'control' | 'timefold';

/**
 * Equivalence class, not the raw backend type-string. Collapses types the backend reports from
 * several directions (teacher- vs class-side of the same clash) so the dedup key can match.
 */
export type FindingKind =
  | 'availabilityViolation'
  | 'overlap'
  | 'capacity'
  | 'placement'
  | 'dependency'
  | 'condition'
  | 'pause'
  | 'structure'
  | 'other';

export type InvolvedResourceType = 'teacher' | 'class' | 'subject' | 'room' | 'group' | 'division';

export type InvolvedResource = {
  type: InvolvedResourceType;
  uuid: string;
  /** Resolved by the store-aware wrapper; the pure layer leaves it undefined. */
  label?: string;
  /** Backend-localized label for the resource's type (e.g. "Lehrkraft"). */
  typeLabel?: string;
  /** Per-resource explanation (e.g. "Lehrer MUE — Mo 08:45 gesperrt"); set later. */
  hint?: string;
};

export type SlotRef = { weekday: string; timegridEntryUuid: string; classUuid?: string };

export type NormalizedFinding = {
  /** Canonical identity from raw backend refs only — never derived data, so checked-state survives plan edits. */
  key: string;
  source: FindingSource;
  kind: FindingKind;
  rawTypes: string[];
  severity?: WarningPriority;
  text: string;
  /** Ready, backend-localized strings for timefold-constraint findings (title/explanation/actionHint). */
  title?: string;
  explanation?: string;
  actionHint?: string;
  involved: InvolvedResource[];
  cardUuids: string[];
  slots: SlotRef[];
};

const KIND_BY_TYPE: Record<string, FindingKind> = {
  // availability violations (both the test "availability*" and control "*NotAvailable" forms)
  availability: 'availabilityViolation',
  availabilityGap: 'availabilityViolation',
  teacherNotAvailable: 'availabilityViolation',
  classNotAvailable: 'availabilityViolation',
  roomNotAvailable: 'availabilityViolation',
  subjectNotAvailable: 'availabilityViolation',
  // overlaps / collisions
  overlappingTeachers: 'overlap',
  overlappingClass: 'overlap',
  overlappingRooms: 'overlap',
  overlappingGroups: 'overlap',
  notMatchingDivision: 'overlap',
  // capacity / over-subscription
  classTooManyCards: 'capacity',
  teacherTooManyCards: 'capacity',
  roomTooManyCards: 'capacity',
  subjectTooManyCards: 'capacity',
  groupTooManyCards: 'capacity',
  lessonTooManyCards: 'capacity',
  teacherTotalTooManyCards: 'capacity',
  roomTotalTooManyCards: 'capacity',
  roomSupplyTooSmall: 'capacity',
  slotBottleneck: 'capacity',
  maxOnUnknownPositionExceeded: 'capacity',
  // placement
  cardCannotBePlaced: 'placement',
  multiSlotCardCannotBePlaced: 'placement',
  pinnedCardConflict: 'placement',
  // dependency
  dependency: 'dependency',
  // pauses / structure
  missingLunchBreak: 'pause',
  doubleLessonsWithoutPauses: 'pause',
  noReliableTimes: 'structure',
  lonelyGroup: 'structure',
  // per-class conditions (GenericWarning.type = keyof ConstraintsType)
  startEnd: 'condition',
  minMaxHours: 'condition',
  maxFreePeriod: 'condition',
  sameEnd: 'condition',
  lunchBreak: 'condition',
  maxOnUnknownPosition: 'condition',
};

export const findingKindForType = (type: string): FindingKind => KIND_BY_TYPE[type] ?? 'other';

const ENTITY_BUCKET_TYPE: Record<keyof InvolvedEntities, InvolvedResourceType> = {
  involvedTeachers: 'teacher',
  involvedClasses: 'class',
  involvedSubjects: 'subject',
  involvedRooms: 'room',
  involvedGroups: 'group',
  notMatchingDivisions: 'division',
};

const involvedResourceKey = (resource: InvolvedResource): string => `${resource.type}:${resource.uuid}`;

/** Flatten the involvedTeachers/Classes/… buckets into involved resources + the cards they touch. */
export function collectInvolved(
  entities: InvolvedEntities,
  resourceTypeLabels?: Record<string, string>,
): { involved: InvolvedResource[]; cardUuids: string[] } {
  const involved: InvolvedResource[] = [];
  const seen = new Set<string>();
  const cardUuids = new Set<string>();
  for (const bucket of Object.keys(ENTITY_BUCKET_TYPE) as (keyof InvolvedEntities)[]) {
    const collisions: Collision[] | undefined = entities[bucket];
    if (!collisions) continue;
    const type = ENTITY_BUCKET_TYPE[bucket];
    for (const collision of collisions) {
      for (const uuid of collision.conflict) {
        const resource: InvolvedResource = { type, uuid, typeLabel: resourceTypeLabels?.[type] };
        const dedupeKey = involvedResourceKey(resource);
        if (!seen.has(dedupeKey)) {
          seen.add(dedupeKey);
          involved.push(resource);
        }
      }
      for (const card of collision.cards) cardUuids.add(card);
    }
  }
  return { involved, cardUuids: [...cardUuids] };
}

/** Canonical key from raw backend references only (sorted involved + card uuids). */
function makeKey(kind: FindingKind, involved: InvolvedResource[], cardUuids: string[]): string {
  const refs = [...new Set([...involved.map((resource) => resource.uuid), ...cardUuids])].sort();
  return `${kind}|${refs.join(',')}`;
}

/** Adapt a control-endpoint result into normalised findings (before dedup). */
export function controlResultToFindings(result: ControlResult): NormalizedFinding[] {
  const findings: NormalizedFinding[] = [];
  const resourceTypeLabels = result.resourceTypeLabels;

  const add = (
    type: string,
    text: string,
    severity: WarningPriority | undefined,
    entities: InvolvedEntities,
    slots: SlotRef[],
    actionHint?: string,
  ) => {
    const kind = findingKindForType(type);
    const { involved, cardUuids } = collectInvolved(entities, resourceTypeLabels);
    findings.push({
      key: makeKey(kind, involved, cardUuids),
      source: 'control',
      kind,
      rawTypes: [type],
      severity,
      text,
      actionHint,
      involved,
      cardUuids,
      slots,
    });
  };

  for (const warning of result.availabilityWarnings ?? []) {
    // Availability warnings carry no text/priority — the UI falls back to the warning-type label.
    add(warning.type, '', undefined, warning, []);
  }
  for (const warning of [...(result.conditionWarnings ?? []), ...(result.dependencyWarnings ?? [])]) {
    const slots: SlotRef[] = (warning.slots ?? []).map((slot) => ({
      weekday: slot.weekday,
      timegridEntryUuid: slot.timegridEntryUuid,
      classUuid: slot.classUuid,
    }));
    add(warning.type, warning.text, warning.priority, warning, slots, warning.actionHint);
  }

  return findings;
}

const SEVERITY_RANK: Record<WarningPriority, number> = { MANDATORY: 0, IMPORTANT: 1, OPTIMIZING: 2 };

/** Higher-severity wins (MANDATORY > IMPORTANT > OPTIMIZING); undefined loses to any defined. */
function mergeSeverity(a?: WarningPriority, b?: WarningPriority): WarningPriority | undefined {
  if (a === undefined) return b;
  if (b === undefined) return a;
  return SEVERITY_RANK[a] <= SEVERITY_RANK[b] ? a : b;
}

const slotKey = (slot: SlotRef): string => `${slot.weekday}|${slot.timegridEntryUuid}|${slot.classUuid ?? ''}`;

/** Merge findings that share a canonical key — keeps every perspective (involved, raw types, slots). */
export function dedupeFindings(findings: NormalizedFinding[]): NormalizedFinding[] {
  const byKey = new Map<string, NormalizedFinding>();

  for (const finding of findings) {
    const existing = byKey.get(finding.key);
    if (!existing) {
      byKey.set(finding.key, {
        ...finding,
        rawTypes: [...finding.rawTypes],
        involved: [...finding.involved],
        cardUuids: [...finding.cardUuids],
        slots: [...finding.slots],
      });
      continue;
    }

    existing.rawTypes = [...new Set([...existing.rawTypes, ...finding.rawTypes])];
    existing.severity = mergeSeverity(existing.severity, finding.severity);
    if (!existing.text && finding.text) existing.text = finding.text;

    const involvedSeen = new Set(existing.involved.map(involvedResourceKey));
    for (const resource of finding.involved) {
      if (!involvedSeen.has(involvedResourceKey(resource))) {
        involvedSeen.add(involvedResourceKey(resource));
        existing.involved.push(resource);
      }
    }

    existing.cardUuids = [...new Set([...existing.cardUuids, ...finding.cardUuids])];

    const slotSeen = new Set(existing.slots.map(slotKey));
    for (const slot of finding.slots) {
      if (!slotSeen.has(slotKey(slot))) {
        slotSeen.add(slotKey(slot));
        existing.slots.push(slot);
      }
    }
  }

  return [...byKey.values()];
}

/** Most severe penalised tier of a Timefold constraint row (scores are negative when violated). */
function timefoldRowSeverity(row: ParsedTimefoldConstraintRow): WarningPriority | undefined {
  if (row.hardScore < 0) return 'MANDATORY';
  if (row.mediumScore < 0) return 'IMPORTANT';
  if (row.softScore < 0) return 'OPTIMIZING';
  return undefined;
}

/**
 * Adapt a parsed Timefold analysis into normalised findings: one finding per *violated*
 * constraint row, with the cards/slots that the per-card findings attribute to it.
 * Satisfied constraints and rewards (non-negative scores) are skipped.
 * Cross-source dedup with control findings needs structured backend refs (Phase 4) and is not
 * attempted here — Timefold findings keep their own identity (`timefold:<row id>`).
 */
export function timefoldAnalysisToFindings(analysis: ParsedTimefoldAnalysis): NormalizedFinding[] {
  const byLabel = new Map<string, { cards: Set<string>; slots: SlotRef[]; slotSeen: Set<string> }>();
  for (const [cardUuid, cardFindings] of Object.entries(analysis.cardsByCardUuid)) {
    for (const cardFinding of cardFindings) {
      let entry = byLabel.get(cardFinding.constraintLabel);
      if (!entry) {
        entry = { cards: new Set(), slots: [], slotSeen: new Set() };
        byLabel.set(cardFinding.constraintLabel, entry);
      }
      entry.cards.add(cardUuid);
      for (const slot of cardFinding.slots ?? []) {
        const ref: SlotRef = { weekday: slot.weekday, timegridEntryUuid: slot.timegridEntryUuid };
        const key = slotKey(ref);
        if (!entry.slotSeen.has(key)) {
          entry.slotSeen.add(key);
          entry.slots.push(ref);
        }
      }
    }
  }

  const findings: NormalizedFinding[] = [];
  for (const row of analysis.constraints) {
    const severity = timefoldRowSeverity(row);
    if (!severity) continue;
    const entry = byLabel.get(row.label);
    findings.push({
      key: `timefold:${row.id}`,
      source: 'timefold',
      kind: 'other',
      rawTypes: [row.label],
      severity,
      text: row.label,
      title: row.localized?.title,
      explanation: row.localized?.explanation,
      actionHint: row.localized?.actionHint,
      involved: [],
      cardUuids: entry ? [...entry.cards] : [],
      slots: entry ? entry.slots : [],
    });
  }
  return findings;
}

export type FindingResolvers = {
  /** True if the card uuid still maps to a real card on the board (snapshot validation). */
  isKnownCard: (uuid: string) => boolean;
  /** Human-readable label for an involved resource (empty string if unknown). */
  resourceLabel: (type: InvolvedResourceType, uuid: string) => string;
};

/** Resolve involved-resource labels and drop card uuids that no longer map to a board card. */
export function resolveFindings(findings: NormalizedFinding[], resolvers: FindingResolvers): NormalizedFinding[] {
  return findings.map((finding) => ({
    ...finding,
    involved: finding.involved.map((resource) => {
      const label = resolvers.resourceLabel(resource.type, resource.uuid);
      return label ? { ...resource, label } : { ...resource };
    }),
    cardUuids: finding.cardUuids.filter((uuid) => resolvers.isKnownCard(uuid)),
  }));
}

/**
 * Single entry point: adapt the available sources, dedup over the canonical key, then resolve
 * labels and validate cards. This is what the findings panel and the readiness summary consume.
 */
export function buildFindings(
  controlResult: ControlResult | null | undefined,
  timefoldAnalysis: ParsedTimefoldAnalysis | null | undefined,
  resolvers: FindingResolvers,
): NormalizedFinding[] {
  const raw = [
    ...(controlResult ? controlResultToFindings(controlResult) : []),
    ...(timefoldAnalysis ? timefoldAnalysisToFindings(timefoldAnalysis) : []),
  ];
  return resolveFindings(dedupeFindings(raw), resolvers);
}
